import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import PageLoader from "./components/PageLoader";

// Marketing pages
import Home from "./pages/Home";
import About from "./pages/About";
import Pricing from "./pages/Pricing";
import Modules from "./pages/Modules";
import Support from "./pages/Support";
import FAQs from "./pages/FAQs";
import Blog from "./pages/Blog";
import Community from "./pages/Community";
import Login from "./pages/Login";
import GetStarted from "./pages/GetStarted";

import SellOnline from "./pages/business/SellOnline";
import CollectPayments from "./pages/business/CollectPayments";
import ManageInventory from "./pages/business/ManageInventory";
import ManageOperations from "./pages/business/ManageOperations";
import AutomateOrders from "./pages/business/AutomateOrders";
import CreateRecords from "./pages/business/CreateRecords";

import Solopreneurs from "./pages/serve/Solopreneurs";
import Medium from "./pages/serve/Medium";
import Large from "./pages/serve/Large";
import Enterprise from "./pages/serve/Enterprise";

import ResellerProgram from "./pages/partners/ResellerProgram";
import ReferralProgram from "./pages/partners/ReferralProgram";

import Terms from "./pages/legal/Terms";
import RefundPolicy from "./pages/legal/RefundPolicy";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import DataPrivacy from "./pages/legal/DataPrivacy";
import Cookies from "./pages/legal/Cookies";

// Tenant flow
import Register from "./pages/auth/Register";
import Payment from "./pages/auth/Payment";
import PaymentPending from "./pages/auth/PaymentPending";
import TenantLogin from "./pages/auth/TenantLogin";

// Admin
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTenants from "./pages/admin/AdminTenants";
import AdminTenantDetail from "./pages/admin/AdminTenantDetail";
import AdminPlans from "./pages/admin/AdminPlans";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminPendingPayments from "./pages/admin/AdminPendingPayments";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminSuperAdmins from "./pages/admin/AdminSuperAdmins";

// Tenant
import TenantLayout from "./pages/tenant/TenantLayout";
import MobileApp from "./pages/mobile/MobileApp";
import AccountDashboard from "./pages/tenant/dashboards/AccountDashboard";
import GenericDashboard from "./pages/tenant/dashboards/GenericDashboard";
import StoreSettings from "./pages/tenant/store/StoreSettings";
import StoreOrders from "./pages/tenant/store/StoreOrders";
import { StoreCollections, StoreBanners } from "./pages/tenant/store/StoreCRUD";
import StoreSubscribers from "./pages/tenant/store/StoreSubscribers";
import StoreMessages from "./pages/tenant/store/StoreMessages";
import StoreVisit from "./pages/tenant/store/StoreVisit";

// Tenant — Products
import AllProducts from "./pages/tenant/products/AllProducts";
import ProductForm from "./pages/tenant/products/ProductForm";
import { OpeningStock, PrintLabels, CountStock, ImportProducts, ImportUpdate } from "./pages/tenant/products/ProductOps";
import { CategoriesPage, SubCategoriesPage, BrandsPage, UnitsPage, WarehousesPage } from "./pages/tenant/products/_shared";

// Tenant — Sales
import { AllSales, CreateSale, ImportSales, Shipments, SalesCounter, CustomerScreen } from "./pages/tenant/sales/SalesPages";
import POS from "./pages/tenant/sales/POS";

// Tenant — People / Users / Purchases / Returns
import {
  Customers, CustomerForm, CustomerImport,
  Suppliers, SupplierForm, SupplierImport,
  UsersPage, RolesPage,
  AllPurchases, CreatePurchase, ImportPurchases,
  AllSalesReturns, CreateSalesReturn,
  AllPurchaseReturns, CreatePurchaseReturn,
} from "./pages/tenant/ops/OpsPages";

// Tenant — Inventory Ops (Adjustment / Quotations / Transfer / Damages)
import {
  AdjustmentsList, AdjustmentForm,
  QuotationsList, QuotationForm,
  TransfersList, TransferForm,
  DamagesList, DamageForm,
} from "./pages/tenant/inventory/InventoryOps";
import StockByWarehouse from "./pages/tenant/products/StockByWarehouse";

// Tenant — HRM
import {
  HRMDashboard, HRMDepartments, HRMDesignations, HRMShifts,
  HRMEmployees, HRMAttendance, HRMLeaves, HRMHolidays, HRMPayroll,
} from "./pages/tenant/hrm/HRMPages";

// Tenant — Accounting
import {
  AccDashboard, AccAccounts, AccJournal, AccTrialBalance, AccPL,
  AccBalanceSheet, AccTax, AccExpenses, AccDeposits, AccCategories, AccMoneyTransfers,
} from "./pages/tenant/accounting/AccountingPages";

// Tenant — Settings / Billing / Reports
import {
  TenantSettings, TenantBilling,
  ReportsHub, ReportSales, ReportPurchases, ReportStock, ReportProfit,
} from "./pages/tenant/settings/SettingsBillingReports";

// Tenant — Assets / Projects / Bookings
import { AssetsPage, ProjectsPage, BookingsPage } from "./pages/tenant/extras/ExtrasPages";

// Master Admin — Blog + Payment Gateway + Email Settings
import { AdminBlog, AdminPaymentGateway } from "./pages/admin/AdminBlogGateway";
import AdminEmailSettings from "./pages/admin/AdminEmailSettings";

// Tenant currency context
import { CurrencyProvider } from "./lib/currency";

// Universal Confirm
import { ConfirmProvider } from "./components/ConfirmDialog";

// Public Storefront
import PublicStorefront from "./pages/store/PublicStorefront";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

// Marketing pages share the public Navbar + Footer
function MarketingShell({ children }) {
  return (
    <>
      <Navbar />
      <main className="pt-20">{children}</main>
      <Footer />
    </>
  );
}

// Bare shell — used for auth/admin/tenant pages
function BareShell({ children }) {
  return <>{children}</>;
}

function Shell() {
  const loc = useLocation();
  const isApp = /^\/(register|payment|payment-pending|payment-success|admin|t|store|m)(\/|$)/.test(loc.pathname);
  const Container = isApp ? BareShell : MarketingShell;

  return (
    <div className="App relative min-h-screen">
      <PageLoader />
      <Container>
        <Routes>
          {/* MARKETING */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/modules" element={<Modules />} />
          <Route path="/support" element={<Support />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/community" element={<Community />} />
          <Route path="/login" element={<Login />} />
          <Route path="/get-started" element={<GetStarted />} />

          <Route path="/business/sell-online" element={<SellOnline />} />
          <Route path="/business/collect-payments" element={<CollectPayments />} />
          <Route path="/business/manage-inventory" element={<ManageInventory />} />
          <Route path="/business/manage-operations" element={<ManageOperations />} />
          <Route path="/business/automate-orders" element={<AutomateOrders />} />
          <Route path="/business/create-records" element={<CreateRecords />} />

          <Route path="/serve/solopreneurs" element={<Solopreneurs />} />
          <Route path="/serve/medium" element={<Medium />} />
          <Route path="/serve/large" element={<Large />} />
          <Route path="/serve/enterprise" element={<Enterprise />} />

          <Route path="/partners/reseller" element={<ResellerProgram />} />
          <Route path="/partners/referral" element={<ReferralProgram />} />

          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/refund" element={<RefundPolicy />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/data-privacy" element={<DataPrivacy />} />
          <Route path="/legal/cookies" element={<Cookies />} />

          {/* TENANT REGISTRATION */}
          <Route path="/register" element={<Register />} />
          <Route path="/payment/:tenantId" element={<Payment />} />
          <Route path="/payment-pending/:reference" element={<PaymentPending />} />
          <Route path="/t/:subdomain/login" element={<TenantLogin />} />

          {/* ADMIN */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="tenants/:tenantId" element={<AdminTenantDetail />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="transactions" element={<AdminTransactions />} />
            <Route path="pending-payments" element={<AdminPendingPayments />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="super-admins" element={<AdminSuperAdmins />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="payment-gateway" element={<AdminPaymentGateway />} />
            <Route path="email-settings" element={<AdminEmailSettings />} />
          </Route>

          {/* TENANT WORKSPACE */}
          <Route path="/t/:subdomain" element={<TenantLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AccountDashboard />} />
            <Route path="dashboard/account" element={<AccountDashboard />} />
            <Route path="dashboard/:kind" element={<GenericDashboard />} />
            <Route path="store/visit" element={<StoreVisit />} />
            <Route path="store/settings" element={<StoreSettings />} />
            <Route path="store/orders" element={<StoreOrders />} />
            <Route path="store/collections" element={<StoreCollections />} />
            <Route path="store/banners" element={<StoreBanners />} />
            <Route path="store/subscribers" element={<StoreSubscribers />} />
            <Route path="store/messages" element={<StoreMessages />} />

            {/* Products */}
            <Route path="products" element={<AllProducts />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:productId/edit" element={<ProductForm />} />
            <Route path="products/import" element={<ImportProducts />} />
            <Route path="products/import-update" element={<ImportUpdate />} />
            <Route path="products/opening-stock" element={<OpeningStock />} />
            <Route path="products/print-labels" element={<PrintLabels />} />
            <Route path="products/count-stock" element={<CountStock />} />
            <Route path="products/categories" element={<CategoriesPage />} />
            <Route path="products/subcategories" element={<SubCategoriesPage />} />
            <Route path="products/brands" element={<BrandsPage />} />
            <Route path="products/units" element={<UnitsPage />} />
            <Route path="products/warehouses" element={<WarehousesPage />} />

            {/* Sales */}
            <Route path="sales" element={<AllSales />} />
            <Route path="sales/new" element={<CreateSale />} />
            <Route path="sales/import" element={<ImportSales />} />
            <Route path="sales/pos" element={<POS />} />
            <Route path="sales/customer-screen" element={<CustomerScreen />} />
            <Route path="sales/shipments" element={<Shipments />} />
            <Route path="sales/counter" element={<SalesCounter />} />

            {/* People */}
            <Route path="people/customers" element={<Customers />} />
            <Route path="people/customers/new" element={<CustomerForm />} />
            <Route path="people/customers/:personId/edit" element={<CustomerForm />} />
            <Route path="people/customers/import" element={<CustomerImport />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="suppliers/new" element={<SupplierForm />} />
            <Route path="suppliers/:personId/edit" element={<SupplierForm />} />
            <Route path="suppliers/import" element={<SupplierImport />} />

            {/* User Management */}
            <Route path="user-management/users" element={<UsersPage />} />
            <Route path="user-management/roles" element={<RolesPage />} />

            {/* Purchases */}
            <Route path="purchases" element={<AllPurchases />} />
            <Route path="purchases/new" element={<CreatePurchase />} />
            <Route path="purchases/import" element={<ImportPurchases />} />

            {/* Sales Return */}
            <Route path="sales-return" element={<AllSalesReturns />} />
            <Route path="sales-return/new" element={<CreateSalesReturn />} />

            {/* Purchases Return */}
            <Route path="purchases-return" element={<AllPurchaseReturns />} />
            <Route path="purchases-return/new" element={<CreatePurchaseReturn />} />

            {/* Inventory Ops — Adjustment / Quotations / Transfer / Damages */}
            <Route path="adjustment" element={<AdjustmentsList />} />
            <Route path="adjustment/new" element={<AdjustmentForm />} />
            <Route path="quotations" element={<QuotationsList />} />
            <Route path="quotations/new" element={<QuotationForm />} />
            <Route path="transfer" element={<TransfersList />} />
            <Route path="transfer/new" element={<TransferForm />} />
            <Route path="stock-by-warehouse" element={<StockByWarehouse />} />
            <Route path="damages" element={<DamagesList />} />
            <Route path="damages/new" element={<DamageForm />} />

            {/* HRM */}
            <Route path="hrm" element={<HRMDashboard />} />
            <Route path="hrm/departments" element={<HRMDepartments />} />
            <Route path="hrm/designations" element={<HRMDesignations />} />
            <Route path="hrm/shifts" element={<HRMShifts />} />
            <Route path="hrm/employees" element={<HRMEmployees />} />
            <Route path="hrm/attendance" element={<HRMAttendance />} />
            <Route path="hrm/leaves" element={<HRMLeaves />} />
            <Route path="hrm/holidays" element={<HRMHolidays />} />
            <Route path="hrm/payrolls" element={<HRMPayroll />} />

            {/* Accounting */}
            <Route path="accounting" element={<AccDashboard />} />
            <Route path="accounting/accounts" element={<AccAccounts />} />
            <Route path="accounting/journal-entries" element={<AccJournal />} />
            <Route path="accounting/trial-balance" element={<AccTrialBalance />} />
            <Route path="accounting/pl" element={<AccPL />} />
            <Route path="accounting/balance-sheet" element={<AccBalanceSheet />} />
            <Route path="accounting/tax-summary" element={<AccTax />} />
            <Route path="accounting/expenses" element={<AccExpenses />} />
            <Route path="accounting/deposits" element={<AccDeposits />} />
            <Route path="accounting/expense-categories" element={<AccCategories kind="expense" title="Expense Categories" />} />
            <Route path="accounting/deposit-categories" element={<AccCategories kind="deposit" title="Deposit Categories" />} />
            <Route path="accounting/money-transfers" element={<AccMoneyTransfers />} />

            {/* Settings / Billing / Reports */}
            <Route path="settings" element={<TenantSettings />} />
            <Route path="billing" element={<TenantBilling />} />
            <Route path="billing/callback" element={<TenantBilling />} />
            <Route path="assets" element={<AssetsPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="reports" element={<ReportsHub />} />
            <Route path="reports/sales" element={<ReportSales />} />
            <Route path="reports/purchases" element={<ReportPurchases />} />
            <Route path="reports/stock" element={<ReportStock />} />
            <Route path="reports/profit" element={<ReportProfit />} />
          </Route>

          {/* PUBLIC STOREFRONT */}
          <Route path="/store/:subdomain" element={<PublicStorefront />} />
          <Route path="/m/*" element={<MobileApp />} />
        </Routes>
      </Container>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ConfirmProvider>
        <ScrollToTop />
        <Shell />
      </ConfirmProvider>
    </BrowserRouter>
  );
}
